/**
 * User: WangMeng
 * Date: 2018-07-16
 * Time: 9:17
 * Desc: 财务舞弊动机与表象
 */
import styles from './motive.scss';
import React, { PureComponent } from 'react';
import { Spin } from 'antd';
import { DangerType } from '../../../../../constants/enum';
import { isNotEmpty, isEmpty } from '../../../../../utils/util';
import { wordLinefeed } from '../../../../../utils/formatter';
import NoData from '../../../../common/noData/NoData';
import EchartBase from '../../../../common/echarts/base/EchartBase';
import { tooltipStyles } from '../../../../common/echarts/config/config';
import { commonRequest } from '../../../common/commons';
import { queryFraudAppearance } from '../../../../../services/financialPicture/overview';

export default class Motive extends PureComponent{
    constructor(props) {
        super(props);
        this.optionsSet = {
            [DangerType.MOTIVE]: {
                name: '舞弊动机',
                itemStyle: {
                    color: '#7BA3DC'
                },
                lineStyle: {
                    color: '#2365C4'
                }
            },
            [DangerType.APPEARANCE]: {
                name: '舞弊表象',
                itemStyle: {
                    color: '#1d8308'
                },
                lineStyle: {
                    color: '#1D8308'
                }
            }
        };
        this.state = {
            loading: false,
            graphOption: {}
        };
    }

    componentDidMount() {
        this.getData();
    }

    getData = () => {
        this.setState({
            loading: true
        });

        queryFraudAppearance(commonRequest()).then(data => {
            data = data || {};
            this.setState({
                graphOption: this.formatGraphOption(data),
                loading: false
            });
        }).catch(() => {
            this.setState({
                loading: false
            });
        });
    };

    // 设置点坐标
    setPosition = (data, options) => {
        if(isEmpty(data)) {
            return;
        }
        const { center, radius = 115, cosBase = 1 } = options;
        let totalAngle = 180; // 点分布的总度数
        let startArc = (360 - totalAngle) / 2 / 180 * Math.PI; // 从多少弧度开始
        let divided = data.length - 1 || 1; // 度数要被分成多少份
        let avgArc = (totalAngle / divided / 180 * Math.PI); // 每份的弧度

        data.forEach((d, i) => {
            let currentArc = avgArc * i + startArc;
            d.x = center.x + (Math.cos(currentArc) * cosBase) * radius;
            d.y = center.y - Math.sin(currentArc) * radius;
        });
    };

    // 格式化option
    formatGraphOption = (data) => {
        let { companyName, list } = data;
        list = list || [];
        let motiveNodes = [], presentNodes = [];
        let width = 1050, height = 330;
        let centerPos = { x: width / 2, y: height / 2 };
        let motivePos = { x: centerPos.x - 190, y: centerPos.y }, presentPos = { x: centerPos.x + 190, y: centerPos.y },
            leftPos = { x: 0, y: 0 }, rightPos = { x: width, y: height }; // 两个确定图大小的点，左上角 & 右下角

        let titleFormat = function (data) {
            return function (param) {
                return param.data.name + (data.length ? '' : '\n(暂无)');
            };
        };

        let nodes = [{ // 公司点
            name: companyName,
            symbolSize: 110,
            ...centerPos,
            itemStyle: {
                color: '#73CDFC'
            },
            label: {
                fontSize: 18
            }
        }, { // 动机点
            name: this.optionsSet[DangerType.MOTIVE].name,
            ...motivePos,
            itemStyle: {
                color: '#2365C4'
            },
            label: {
                fontSize: 14,
                formatter: titleFormat(motiveNodes)
            }
        }, { // 表象点
            name: this.optionsSet[DangerType.APPEARANCE].name,
            ...presentPos,
            itemStyle: {
                color: '#DC5712'
            },
            label: {
                fontSize: 14,
                formatter: titleFormat(presentNodes)
            }
        }, { // 这个点是占位置的，无实际意义，防止数据不同时整个图的布局随意缩放
            name: 'left',
            ...leftPos,
            symbolSize: 0
        }, { // 同上
            name: 'right',
            ...rightPos,
            symbolSize: 0
        }];
        let links = [{
            source: companyName,
            target: this.optionsSet[DangerType.MOTIVE].name,
            lineStyle: {
                color: '#2365C4'
            }
        }, {
            source: companyName,
            target: this.optionsSet[DangerType.APPEARANCE].name,
            lineStyle: {
                color: '#F99248'
            }
        }];

        list.forEach(item => {
            let set = this.optionsSet[item.type];
            let node = {
                name: item.exName,
                explain: item.explain,
                itemStyle: set.itemStyle,
                tooltip: {
                    show: true,
                    formatter: function(param) {
                        return wordLinefeed(param.data.explain, 15, '<br />');
                    }
                }
            };
            let link = {
                source: item.exName,
                target: set.name,
                lineStyle: set.lineStyle
            };
            if(item.type === DangerType.MOTIVE) {
                motiveNodes.push(node);
            } else {
                presentNodes.push(node);
            }
            links.push(link);
        });

        this.setPosition(motiveNodes, { center: motivePos, cosBase: 1 });
        this.setPosition(presentNodes, { center: presentPos, cosBase: -1 });

        return {
            tooltip: {
                show: false,
                ...tooltipStyles
            },
            animationDurationUpdate: 1500,
            animationEasingUpdate: 'quinticInOut',
            series: [
                {
                    type: 'graph',
                    symbolSize: 80,
                    layout: 'none',
                    width, // 配合上面两个占位点 固定图大小
                    height,
                    symbol: 'path://M664,613.031l-20,35.01H604l-20-35.01,20-35.011h40Z',
                    symbolKeepAspect: true,
                    label: {
                        show: true,
                        fontSize: 12,
                        textBorderColor: 'transparent',
                        formatter: function(param) {
                            return wordLinefeed(param.name, 4);
                        }
                    },
                    lineStyle: {
                        opacity: 1,
                        width: 1
                    },
                    data: nodes.concat(motiveNodes, presentNodes),
                    links
                }
            ]
        };
    };

    render() {
        const { loading, graphOption } = this.state;

        return (
            <div className={styles.graphChart}>
                {
                    loading ?
                        <Spin /> :
                        isNotEmpty(graphOption) ?
                            <EchartBase option={graphOption}/> : <NoData />
                }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/financialPicture/overview/subComponents/fraud/Motive.jsx